import { Text } from '@app/components/core/typography/Text'
import { LexicalRichHtmlInputToolbar } from '@app/components/core/input/lexicalRichInput/Toolbar'
import {
  editorEditableStyle,
  editorShellStyle,
} from '@app/components/core/input/lexicalRichInput/styles/editorStyles'
import { HtmlSyncPlugin } from '@app/components/core/input/lexicalRichInput/plugins/HtmlSyncPlugin'
import { HistoryPlugin } from '@app/components/core/input/lexicalRichInput/plugins/HistoryPlugin'
import { ListPlugin } from '@app/components/core/input/lexicalRichInput/plugins/ListPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { Style, styled } from 'inlines'
import { InputField } from '../InputField'
import { type FocusEventHandler, useId, useMemo } from 'react'
import { useTranslations } from '@i18n/useLanguage'
import { lexicalTheme } from './styles/lexicalTheme'
import { ListItemNode, ListNode } from '@lexical/list'
import { LinkNode } from '@lexical/link'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'

type LexicalRichHtmlInputInnerProps = {
  value: string
  setValue: (html: string) => void
  placeholder?: string
  onBlur?: FocusEventHandler<HTMLDivElement>
  hasError: boolean
  enableLinks?: boolean
  borderlessShell?: boolean
}

const LexicalRichHtmlInputInner = ({
  value,
  setValue,
  placeholder,
  onBlur,
  hasError,
  enableLinks,
  borderlessShell,
}: LexicalRichHtmlInputInnerProps) => {
  const t = useTranslations().app.dashboard.pageContentEditor

  return (
    <styled.div style={editorShellStyle(hasError, { borderless: borderlessShell })}>
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            className="LexicalRichHtmlInput-editable"
            style={editorEditableStyle}
            aria-placeholder={placeholder ?? t.bodyPlaceholder}
            placeholder={null}
            onBlur={onBlur}
          />
        }
        placeholder={
          <Text.Medium
            style={{
              color: 'var(--gray-300)',
              position: 'absolute',
              pointerEvents: 'none',
              userSelect: 'none',
              top: '0.5rem',
              left: '0.5rem',
              zIndex: 0,
            }}
          >
            {placeholder ?? t.bodyPlaceholder}
          </Text.Medium>
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <HistoryPlugin />
      <ListPlugin />
      {enableLinks ? <LinkPlugin /> : null}
      <HtmlSyncPlugin value={value} setValue={setValue} />
      <LexicalRichHtmlInputToolbar enableLinks={enableLinks} />
    </styled.div>
  )
}

export type LexicalRichHtmlInputProps = {
  value: string
  setValue: (value: string) => void
  placeholder?: string
  label?: string
  labelStyle?: Style
  style?: Style
  errorMessage?: string
  onBlur?: FocusEventHandler<HTMLDivElement>
  enableLinks?: boolean
  borderlessShell?: boolean
}

export const LexicalRichHtmlInput = ({
  value,
  setValue,
  placeholder,
  label,
  labelStyle,
  style,
  errorMessage,
  onBlur,
  enableLinks,
  borderlessShell,
}: LexicalRichHtmlInputProps) => {
  const hasError = !!errorMessage
  const editorNamespaceId = useId().replace(/:/g, '')

  const initialConfig = useMemo(
    () => ({
      namespace: `LexicalRichHtmlInput-${editorNamespaceId}`,
      theme: lexicalTheme,
      // LinkNode is always registered so saved <a> HTML round-trips on reload.
      // Link creation UI remains gated behind enableLinks.
      nodes: [ListNode, ListItemNode, LinkNode],
      onError: (e: Error) => {
        // Lexical requires an onError handler; we still want an explicit signal in dev.
        console.error(e)
      },
    }),
    [editorNamespaceId],
  )

  const shellStyle: Style = {
    minHeight: '8rem',
    ...style,
  }

  return (
    <InputField
      label={label}
      labelStyle={labelStyle}
      errorMessage={errorMessage}
      style={shellStyle}
    >
      <LexicalComposer initialConfig={initialConfig}>
        <LexicalRichHtmlInputInner
          value={value}
          setValue={setValue}
          placeholder={placeholder}
          onBlur={onBlur}
          hasError={hasError}
          enableLinks={enableLinks}
          borderlessShell={borderlessShell}
        />
      </LexicalComposer>
    </InputField>
  )
}
